/**
 * Google Cloud STT Service (Client-Side)
 * 
 * Provides speech-to-text using Google Cloud Speech-to-Text batch recognition.
 * Uses Firebase callable function for secure API access.
 * 
 * Key advantage over Deepgram: Supports Swahili (sw-KE, sw-TZ).
 * 
 * Architecture: Accumulates audio chunks, then sends to batch API after silence detection.
 * This approach works well for translation mode where there are natural pauses between speakers.
 * 
 * Used by TranslationSTTManager when Swahili is one of the translation languages.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// Supported languages for Google Cloud STT
const GOOGLE_SUPPORTED_LANGUAGES = [
  'en-US', 'en-GB', 'en-AU',
  'es-ES', 'es-MX', 'es-US',
  'fr-FR', 'fr-CA',
  'sw-KE', 'sw-TZ',  // Swahili - key language not supported by Deepgram
  'de-DE',
  'pt-BR', 'pt-PT',
  'it-IT',
  'ja-JP',
  'zh-CN', 'zh-TW',
  'ar-SA',
  'hi-IN',
  'ru-RU',
];

// Audio buffer settings
const SILENCE_THRESHOLD = 1500;  // ms of silence before triggering transcription
const MIN_AUDIO_DURATION = 500;  // Minimum ms of audio before attempting transcription
const MAX_BUFFER_DURATION = 30000;  // Max 30 seconds of audio before forced transcription

/**
 * Google Cloud STT Service Class
 * 
 * Uses batch recognition with audio buffering and silence detection.
 * Optimized for translation mode with natural pauses between speakers.
 */
class GoogleSTTService {
  constructor() {
    this.isConnected = false;
    this.isConnecting = false;
    
    // Callbacks
    this.onTranscript = null;
    this.onInterimTranscript = null;
    this.onError = null;
    this.onConnectionChange = null;
    
    // Configuration
    this.config = {
      languageCode: 'en-US',
      alternativeLanguageCodes: [],  // For automatic language detection
      encoding: 'LINEAR16',
      sampleRateHertz: 16000,
      enableAutomaticPunctuation: true,
    };
    
    // Audio buffering
    this.audioBuffer = [];
    this.bufferStartTime = null;
    this.lastAudioTime = null;
    this.silenceTimer = null;
    this.isProcessing = false;
    
    // Firebase functions reference
    this.functions = getFunctions();
    this.googleSTTTranscribeFn = httpsCallable(this.functions, 'googleSTTTranscribe');
  }

  /**
   * Check if a language is supported
   * @param {string} language - Language code
   * @returns {boolean}
   */
  supportsLanguage(language) {
    return GOOGLE_SUPPORTED_LANGUAGES.some(
      lang => language.startsWith(lang.split('-')[0]) || lang === language
    );
  }

  /**
   * Check if this is a Swahili language
   * @param {string} language - Language code
   * @returns {boolean}
   */
  isSwahili(language) {
    return language.startsWith('sw');
  }

  /**
   * Initialize the service with configuration
   * @param {Object} config - Configuration options
   */
  configure(config = {}) {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Set callback handlers
   * @param {Object} handlers - { onTranscript, onInterimTranscript, onError, onConnectionChange }
   */
  setHandlers(handlers) {
    if (handlers.onTranscript) this.onTranscript = handlers.onTranscript;
    if (handlers.onInterimTranscript) this.onInterimTranscript = handlers.onInterimTranscript;
    if (handlers.onError) this.onError = handlers.onError;
    if (handlers.onConnectionChange) this.onConnectionChange = handlers.onConnectionChange;
  }

  /**
   * Connect to Google Cloud STT service
   * For batch mode, this just initializes the service and marks as ready
   * @param {string[]} languages - Array of language codes for multilingual recognition
   * @returns {Promise<boolean>} Success status
   */
  async connect(languages = ['en-US']) {
    if (this.isConnected || this.isConnecting) {
      return true;
    }

    this.isConnecting = true;
    this.onConnectionChange?.('connecting');

    try {
      // Set primary language and alternatives for multilingual detection
      const primaryLanguage = languages[0];
      const alternativeLanguages = languages.slice(1);
      
      this.config.languageCode = primaryLanguage;
      this.config.alternativeLanguageCodes = alternativeLanguages;
      
      
      // Clear any existing buffer
      this._clearBuffer();
      
      // Mark as connected
      this.isConnected = true;
      this.isConnecting = false;
      this.onConnectionChange?.('connected');
      
      return true;

    } catch (error) {
      console.error('[GoogleSTT] Initialization error:', error);
      this.isConnecting = false;
      this.onError?.(error);
      this.onConnectionChange?.('error');
      return false;
    }
  }

  /**
   * Send audio data for transcription
   * Audio is buffered and sent to batch API after silence detection
   * @param {ArrayBuffer|Uint8Array} audioData - Raw audio data (LINEAR16 PCM)
   * @returns {boolean} Whether audio was accepted
   */
  sendAudio(audioData) {
    if (!this.isConnected) {
      return false;
    }

    const now = Date.now();
    
    // Initialize buffer timing if this is the first chunk
    if (!this.bufferStartTime) {
      this.bufferStartTime = now;
    }
    
    // Add to buffer
    this.audioBuffer.push(audioData);
    this.lastAudioTime = now;
    
    // Reset silence timer
    this._resetSilenceTimer();
    
    // Check if we've exceeded max buffer duration
    const bufferDuration = now - this.bufferStartTime;
    if (bufferDuration >= MAX_BUFFER_DURATION) {
      this._processBuffer();
    }
    
    return true;
  }

  /**
   * Reset the silence detection timer
   * @private
   */
  _resetSilenceTimer() {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
    }
    
    this.silenceTimer = setTimeout(() => {
      this._onSilenceDetected();
    }, SILENCE_THRESHOLD);
  }

  /**
   * Called when silence is detected (no audio for SILENCE_THRESHOLD ms)
   * @private
   */
  _onSilenceDetected() {
    const bufferDuration = this.bufferStartTime ? Date.now() - this.bufferStartTime : 0;
    
    if (bufferDuration >= MIN_AUDIO_DURATION && this.audioBuffer.length > 0) {
      this._processBuffer();
    }
  }

  /**
   * Process the accumulated audio buffer
   * @private
   */
  async _processBuffer() {
    if (this.isProcessing || this.audioBuffer.length === 0) {
      return;
    }

    this.isProcessing = true;
    
    // Stop silence timer
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }

    try {
      // Combine all audio chunks into a single buffer
      const combinedAudio = this._combineAudioChunks(this.audioBuffer);
      
      // Convert to base64 for transmission
      const audioBase64 = this._arrayBufferToBase64(combinedAudio);
      
      // Clear buffer before async call
      this._clearBuffer();

      // Send to Firebase function for transcription
      const result = await this.googleSTTTranscribeFn({
        audio: audioBase64,
        languageCode: this.config.languageCode,
        alternativeLanguageCodes: this.config.alternativeLanguageCodes,
        enableAutomaticPunctuation: this.config.enableAutomaticPunctuation,
      });

      const { transcript, confidence, languageCode } = result.data;

      if (transcript && transcript.trim()) {
        
        // Call the onTranscript callback with the result
        this.onTranscript?.({
          transcript: transcript.trim(),
          text: transcript.trim(),  // Alias for compatibility
          confidence: confidence || 0.9,
          isFinal: true,
          languageCode: languageCode || this.config.languageCode,
        });
      } else {
      }

    } catch (error) {
      console.error('[GoogleSTT] Transcription error:', error);
      this.onError?.(error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Combine multiple audio chunks into a single ArrayBuffer
   * @param {Array<ArrayBuffer|Uint8Array>} chunks - Array of audio chunks
   * @returns {ArrayBuffer} Combined audio
   * @private
   */
  _combineAudioChunks(chunks) {
    // Calculate total length
    let totalLength = 0;
    for (const chunk of chunks) {
      totalLength += chunk.byteLength || chunk.length;
    }

    // Create combined buffer
    const combined = new Uint8Array(totalLength);
    let offset = 0;

    for (const chunk of chunks) {
      const data = chunk instanceof ArrayBuffer ? new Uint8Array(chunk) : chunk;
      combined.set(data, offset);
      offset += data.length;
    }

    return combined.buffer;
  }

  /**
   * Convert ArrayBuffer to base64 string
   * @param {ArrayBuffer} buffer - The buffer to convert
   * @returns {string} Base64 encoded string
   * @private
   */
  _arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Clear the audio buffer
   * @private
   */
  _clearBuffer() {
    this.audioBuffer = [];
    this.bufferStartTime = null;
    this.lastAudioTime = null;
    
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  /**
   * Force processing of any buffered audio
   * Call this when you want to get transcription immediately (e.g., before TTS)
   */
  async flushBuffer() {
    if (this.audioBuffer.length > 0) {
      await this._processBuffer();
    }
  }

  /**
   * Disconnect from Google STT
   */
  disconnect() {
    
    // Process any remaining audio
    if (this.audioBuffer.length > 0 && !this.isProcessing) {
      this._processBuffer();
    }
    
    // Clear everything
    this._clearBuffer();
    this.isConnected = false;
    this.isConnecting = false;
    this.isProcessing = false;
    this.onConnectionChange?.('disconnected');
  }

  /**
   * Get connection status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      isProcessing: this.isProcessing,
      bufferChunks: this.audioBuffer.length,
      bufferDuration: this.bufferStartTime ? Date.now() - this.bufferStartTime : 0,
      config: this.config,
      mode: 'batch',  // Indicate this is batch mode
    };
  }
}

// Export singleton instance
export const googleSTTService = new GoogleSTTService();

// Export class for testing
export { GoogleSTTService };

export default googleSTTService;
